/**
 * Licence note + parallel launch-out, pinned to the foot of the panel.
 *
 * Collapsed to one slim row by default: the panel opens verse after verse
 * and the explanation only needs reading once, but the reason has to stay
 * reachable — we can never show NASB wording ourselves.
 *
 * Bible Gateway (not BibleHub, which skjv-mobile used) because pinning
 * `version=KJV;NASB` gives a true two-column KJV-vs-NASB view carrying
 * the Lockman copyright — BibleHub stacks ~20 translations with no
 * prominent copyright, which is the opposite of the point here.
 */

import { useState } from 'react';
import { ChevronRightIcon } from '@components/icons';

interface NasbDisclaimerProps {
  reference: string;
}

/** Pinned to exactly the two texts this panel compares */
const PARALLEL_VERSIONS = 'KJV;NASB';

export function parallelUrlFor(reference: string): string {
  const search = encodeURIComponent(reference);
  return `https://www.biblegateway.com/passage/?search=${search}&version=${PARALLEL_VERSIONS}`;
}

export function NasbDisclaimer({ reference }: NasbDisclaimerProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="verse-analysis-disclaimer">
      <div className="verse-analysis-disclaimer-row">
        <button
          className="verse-analysis-disclaimer-toggle"
          onClick={() => setIsOpen((open) => !open)}
          aria-expanded={isOpen}
        >
          <ChevronRightIcon
            className={`verse-analysis-disclaimer-chevron ${isOpen ? 'is-open' : ''}`}
          />
          Why no NASB wording?
        </button>
        <a
          className="verse-analysis-parallel-link"
          href={parallelUrlFor(reference)}
          target="_blank"
          rel="noopener noreferrer"
        >
          Compare side by side ↗
        </a>
      </div>

      {isOpen && (
        <p className="verse-analysis-disclaimer-body">
          The NASB text is under copyright, so this comparison is built from Strong&apos;s
          numbers alone. The bottom row shows dictionary glosses for the critical text&apos;s
          words — indicative meanings, not the NASB&apos;s own translation, and words its
          source leaves untagged show as ∅. Bible Gateway carries both translations in
          parallel with the publisher&apos;s copyright.
        </p>
      )}
    </div>
  );
}
